import { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { RandomAwardType, useExecuteSpinWheelGameMutation } from '@ocx/graphql';
import { OfferVisibleStatus, SpinWheelGameOffer } from '../../../../modules/offers/offer.types';
import { logInDev } from '../../../../lib/logger/logger';
import { useShowGenericErrorSnackbar } from '../../../../hooks/useShowGenericErrorSnackbar';
import { useInfoActionSheet } from '../../../../components/modals/InfoActionSheet';
import {
  getSpinWheelGameGenericErrorPopupParams,
  getSpinWheelGamePrizePopupParams,
  mapSegmentToSector,
  showConfetti,
} from '../spin-wheel-game.utils';
import { SpinWheelGameEarningMechanism } from '../../../../modules/offers/earning-mechanism.types';
import { useGameplayVoucher } from '../../../../modules/games/hooks/use-gameplay-voucher';

type UseSpinWheelGameControllerParams = { offer: SpinWheelGameOffer; onLastGameplayEnd(): void };

export const useSpinWheelVoucheredGameController = (params: UseSpinWheelGameControllerParams) => {
  const { offer, onLastGameplayEnd } = params;
  const {
    id: offerId,
    voucherCount,
    visibleStatus,
    earningMechanism: { prizePool },
  } = offer;

  const intl = useIntl();
  const showGenericErrorSnackbar = useShowGenericErrorSnackbar();
  const { open: openInfoActionSheet } = useInfoActionSheet();

  const [isSpinning, setIsSpinning] = useState(false);
  const [winningSectorIndex, setWinningSectorIndex] = useState<number | undefined>();
  const [isActionButtonDisabled, setIsActionButtonDisabled] = useState(false);
  const [gameplayCount, setGameplayCount] = useState(voucherCount);
  const isGameLimitReached = [OfferVisibleStatus.MemberLimit, OfferVisibleStatus.GlobalLimit].includes(visibleStatus);

  const [executeSpinWheelGame] = useExecuteSpinWheelGameMutation({
    refetchQueries: ['currentCustomer', 'offer'],
    awaitRefetchQueries: true,
    onCompleted: (data) => {
      setIsSpinning(false);
      if (!data.result) {
        showGenericErrorSnackbar();
        return;
      }
      setWinningSectorIndex(data.result.sequenceNumber - 1); // NOTE: sequenceNumber starts from 1
    },
    onError: () => {
      setIsSpinning(false);
      setWinningSectorIndex(-1); // reset the wheel to the start position, without any callbacks
      openInfoActionSheet(getSpinWheelGameGenericErrorPopupParams(intl));
    },
  });

  const onPrizePopupClosed = useCallback(() => {
    // Close game when voucherCount 0 and no purchasable offer provided for vouchered game
    if ((voucherCount === 0 && !offer.earningMechanism.gameplayVoucher) || isGameLimitReached) {
      onLastGameplayEnd();
    }
  }, [isGameLimitReached, offer.earningMechanism.gameplayVoucher, voucherCount, onLastGameplayEnd]);

  const handleSpinningEnd = useCallback(
    (sectorIndex: number) => {
      setIsActionButtonDisabled(false);
      setGameplayCount(voucherCount);

      const prize = prizePool[sectorIndex];
      if (!prize) {
        return; // error happened, the wheel stopped with a -1 index, or index is outside the range
      }
      if (![RandomAwardType.NoAward, RandomAwardType.TryAgain].includes(prize.awardType)) {
        showConfetti();
      }

      openInfoActionSheet({
        ...getSpinWheelGamePrizePopupParams(prize, intl),
        onExited: onPrizePopupClosed,
      });
    },
    [intl, onPrizePopupClosed, openInfoActionSheet, prizePool, voucherCount],
  );

  const handlePlayClick = useCallback(async () => {
    setIsActionButtonDisabled(true);
    setIsSpinning(true);
    executeSpinWheelGame({ variables: { offerId } }).catch(logInDev);
  }, [executeSpinWheelGame, offerId]);

  const gameplayVoucher = useGameplayVoucher({
    offer,
    onPurchased: handlePlayClick, // Execute game when voucher purchased
    onError: showGenericErrorSnackbar,
  });
  const handlePurchaseClick = useCallback(() => {
    if (!gameplayVoucher) {
      return;
    }
    gameplayVoucher.purchase();
  }, [gameplayVoucher]);

  return useMemo(
    () => ({
      gameplayCount,
      handlePlayClick,
      handleSpinningEnd,
      isSpinning,
      winningSectorIndex,
      isActionButtonDisabled,
      // Purchase Voucher
      isPurchaseAvailable: Boolean(gameplayVoucher),
      isPurchasing: gameplayVoucher?.isPurchasing ?? false,
      handlePurchaseClick,
      purchasePrice: gameplayVoucher?.price ?? null,
      isGameLimitReached,
    }),
    [
      gameplayCount,
      gameplayVoucher,
      handlePlayClick,
      handlePurchaseClick,
      handleSpinningEnd,
      isActionButtonDisabled,
      isSpinning,
      winningSectorIndex,
      isGameLimitReached,
    ],
  );
};

export const useSpinWheelGameSectorsHook = (
  prizePool: SpinWheelGameEarningMechanism['prizePool'],
  sectorsConfiguration?: Array<{ icon?: string; color?: string }>,
) => {
  return {
    sectors: prizePool.map((prizePoolItem, index) => mapSegmentToSector(prizePoolItem, sectorsConfiguration?.[index])),
  };
};
